import { apiService } from '@/api/apiConfig';

// Types for revenue categories
export interface RevenueCategoryData {
  name: string;
  description?: string;
}

export interface RevenueCategoryResponse {
  id: string;
  name: string;
  description?: string;
}

export interface RevenueCategoriesResponse {
  data: RevenueCategoryResponse[];
}

const handleRevenueCategoryError = (error: any): Error => {
  if (error.response?.data?.message) {
    return new Error(`Revenue category operation failed: ${error.response.data.message}`);
  }
  return error instanceof Error ? error : new Error('Revenue category operation failed');
};

export const revenueCategoryService = {
  getRevenueCategories: async (budgetId: string): Promise<RevenueCategoriesResponse> => {
    try {
      return await apiService.request<RevenueCategoriesResponse>({
        method: 'GET',
        url: `/budget/${budgetId}/revenue-category`
      });
    } catch (error) {
      throw handleRevenueCategoryError(error);
    }
  },

  getRevenueCategory: async (budgetId: string, categoryId: string): Promise<RevenueCategoryResponse> => {
    try {
      return await apiService.request<RevenueCategoryResponse>({
        method: 'GET',
        url: `/budget/${budgetId}/revenue-category/${categoryId}`
      });
    } catch (error) {
      throw handleRevenueCategoryError(error);
    }
  },

  createRevenueCategory: async (
    budgetId: string,
    categoryData: RevenueCategoryData
  ): Promise<RevenueCategoryResponse> => {
    try {
      return await apiService.request<RevenueCategoryResponse>({
        method: 'POST',
        url: `/budget/${budgetId}/revenue-category`,
        data: categoryData,
        headers: {
          'Content-Type': 'application/json'
        }
      });
    } catch (error) {
      throw handleRevenueCategoryError(error);
    }
  },

  activateRevenueCategories: async (budgetId: string, data: any): Promise<any> => {
    try {
      return await apiService.request<any>({
        method: 'POST',
        url: `/budget/${budgetId}/revenue-category/activate`,
        data,
        headers: {
          'Content-Type': 'application/json'
        }
      });
    } catch (error) {
      throw handleRevenueCategoryError(error);
    }
  },

  deactivateRevenueCategory: async (budgetId: string, data: any): Promise<any> => {
    try {
      return await apiService.request<any>({
        method: 'POST',
        url: `/budget/${budgetId}/revenue-category/deactivate`,
        data,
        headers: {
          'Content-Type': 'application/json'
        }
      });
    } catch (error) {
      throw handleRevenueCategoryError(error);
    }
  },

  updateRevenueCategory: async (
    budgetId: string,
    categoryId: string,
    categoryData: RevenueCategoryData
  ): Promise<RevenueCategoryResponse> => {
    try {
      return await apiService.request<RevenueCategoryResponse>({
        method: 'POST',
        url: `/budget/${budgetId}/revenue-category/${categoryId}`,
        data: categoryData,
        headers: {
          'Content-Type': 'application/json'
        }
      });
    } catch (error) {
      throw handleRevenueCategoryError(error);
    }
  },

  deleteRevenueCategory: async (budgetId: string, categoryId: string): Promise<void> => {
    try {
      return await apiService.request<void>({
        method: 'DELETE',
        url: `/budget/${budgetId}/revenue-category/${categoryId}`
      });
    } catch (error) {
      throw handleRevenueCategoryError(error);
    }
  }
};
